import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Editor } from "primereact/editor";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import DetailPopup from "../../../components/DetailPopup/DetailPopup";
import PhotoZoom from "../../../components/PhotoZoom/PhotoZoom";
import { ProjelerService } from "../../../services/projeler.service";
import { TemaService } from "../../../services/tema.service";
import { Proje, ProjeFoto, Tema } from "../../../services/types";
import { editorHeader, fixQuillLists } from "../../../utils/commonUtil";

const stripHtml = (html: string) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const validationSchema = Yup.object().shape({
  baslik: Yup.string()
    .required("Başlık alanı zorunludur")
    .max(100, "Başlık en fazla 100 karakter olmalıdır"),
  aciklama: Yup.string().required("Açıklama alanı zorunludur"),
  projeYer: Yup.string()
    .required("Proje yeri alanı zorunludur")
    .max(150, "Proje yeri en fazla 150 karakter olmalıdır"),
  projeYil: Yup.string().required("Proje yılı alanı zorunludur"),
  projeIsbirlik: Yup.string()
    .required("Proje işbirliği alanı zorunludur")
    .test("html-length", "Proje işbirliği çok uzun", (value) => {
      const plainText = stripHtml(value);
      return plainText.length <= 200;
    }),
  projeEkip: Yup.string()
    .required("Proje ekibi alanı zorunludur")
    .test("html-length", "Proje ekibi çok uzun", (value) => {
      const plainText = stripHtml(value);
      return plainText.length <= 200;
    }),
  insaatAlan: Yup.string()
    .required("İnşaat alanı zorunludur")
    .max(100, "İnşaat alanı en fazla 100 karakter olmalıdır"),
  temaTipId: Yup.string().required("Tema seçimi zorunludur"),
});

const ProjeYonetimi: React.FC = () => {
  const [items, setItems] = useState<Proje[]>([]);
  const [temaList, setTemaList] = useState<Tema[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [fotoDialogVisible, setFotoDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Proje | null>(null);
  const [selectedFotos, setSelectedFotos] = useState<ProjeFoto[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [photoZoomVisible, setPhotoZoomVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [detailPopupVisible, setDetailPopupVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<Record<string, string>>(
    {}
  );

  const [formData, setFormData] = useState({
    baslik: "",
    aciklama: "",
    projeYer: "",
    projeYil: "",
    projeIsbirlik: "",
    projeEkip: "",
    insaatAlan: "",
    temaTipId: "",
    kapakGorsel: "",
    yatayGorsel: "",
    kapakFotoFile: null as File | null,
    yatayFotoFile: null as File | null,
  });

  useEffect(() => {
    loadData();
    loadTemaList();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await ProjelerService.listele();
      if (response.data) {
        setItems(response.data);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Veriler yüklenirken bir hata oluştu",
      });
    }
    setLoading(false);
  };

  const loadTemaList = async () => {
    try {
      const response = await TemaService.listele();
      if (response.data) {
        // gizli temayı filtrele
        const filteredTemaList = response.data.filter(
          (item) => !(item.baslik === "gizli" && item.aciklama === "gizli")
        );
        setTemaList(filteredTemaList);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Tema listesi yüklenirken bir hata oluştu",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      if (editMode && selectedItem) {
        await ProjelerService.guncelle({
          id: selectedItem.id,
          ...formData,
        });
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Proje başarıyla güncellendi",
        });
      } else {
        if (!formData.kapakFotoFile || !formData.yatayFotoFile) {
          toast.current?.show({
            severity: "error",
            summary: "Hata",
            detail: "Lütfen tüm görselleri seçiniz",
          });
          return;
        }
        await ProjelerService.ekle(formData);
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Proje başarıyla eklendi",
        });
      }
      hideDialog();
      loadData();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Doğrulama Hatası",
            detail: err.message,
          });
        });
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "İşlem sırasında bir hata oluştu",
      });
    }
  };

  const showFotoDialog = async (item: Proje) => {
    setSelectedItem(item);
    try {
      const response = await ProjelerService.projeFotolariDetay(item.id);
      if (response.data) {
        setSelectedFotos(response.data);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Fotoğraflar yüklenirken bir hata oluştu",
      });
    }
    setFotoDialogVisible(true);
  };

  const handleFotoUpload = async (event: { files: File[] }) => {
    if (!selectedItem) return;

    try {
      await ProjelerService.projeFotoEkle({
        projeId: selectedItem.id,
        sira: selectedFotos.length + 1,
        foto: event.files[0],
      });

      // Fotoğraf listesini güncelle
      const response = await ProjelerService.projeFotolariDetay(
        selectedItem.id
      );
      if (response.data) {
        setSelectedFotos(response.data);
      }

      //XXX Fotoğraf Ekle Aynı resim kalıyor onun sorunu çözelim!!!

      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Fotoğraf başarıyla yüklendi",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Fotoğraf yüklenirken bir hata oluştu",
      });
    }
  };

  const handleFotoDelete = async (fotoId: string) => {
    try {
      await ProjelerService.projeFotoSil(fotoId);

      if (selectedItem) {
        const response = await ProjelerService.projeFotolariDetay(
          selectedItem.id
        );
        if (response.data) {
          setSelectedFotos(response.data);
        }
      }

      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Fotoğraf başarıyla silindi",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Fotoğraf silinirken bir hata oluştu",
      });
    }
  };

  // Eksik yardımcı fonksiyonlar
  const showDialog = (item?: Proje) => {
    if (item) {
      setEditMode(true);
      setSelectedItem(item);
      setFormData({
        baslik: item.baslik,
        aciklama: item.aciklama,
        projeYer: item.projeYer,
        projeYil: item.projeYil,
        projeIsbirlik: item.projeIsbirlik,
        projeEkip: item.projeEkip,
        insaatAlan: item.insaatAlan,
        temaTipId: item.temaTipId,
        kapakGorsel: item.kapakGorsel,
        yatayGorsel: item.yatayGorsel,
        kapakFotoFile: null,
        yatayFotoFile: null,
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormData({
        baslik: "",
        aciklama: "",
        projeYer: "",
        projeYil: "",
        projeIsbirlik: "",
        projeEkip: "",
        insaatAlan: "",
        temaTipId: "",
        kapakGorsel: "",
        yatayGorsel: "",
        kapakFotoFile: null,
        yatayFotoFile: null,
      });
    }
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditMode(false);
    setSelectedItem(null);
  };

  const confirmDelete = (item: Proje) => {
    confirmDialog({
      message: "Bu projeyi silmek istediğinizden emin misiniz?",
      header: "Silme Onayı",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: () => handleDelete(item.id),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await ProjelerService.sil(id);
      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Proje başarıyla silindi",
      });
      loadData();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Silme işlemi sırasında bir hata oluştu",
      });
    }
  };

  const actionBodyTemplate = (rowData: Proje) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-images"
          rounded
          outlined
          onClick={() => showFotoDialog(rowData)}
        />
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          onClick={() => showDialog(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    );
  };

  const rowExpansionTemplate = (data: Proje) => {
    return (
      <div className="p-3">
        <h5>Proje Detayları</h5>
        <div className="grid">
          <div className="field md:col-6">
            <div className="field">
              <label className="font-bold">Açıklama:</label>
              <div
                dangerouslySetInnerHTML={{
                  __html: fixQuillLists(data.aciklama),
                }}
              />
            </div>
            <div className="field">
              <label className="font-bold">Proje Yılı:</label>
              <div>{new Date(data.projeYil).toLocaleDateString()}</div>
            </div>
            <div className="field">
              <label className="font-bold">Proje Yeri:</label>
              <div>{data.projeYer}</div>
            </div>
            <div className="field">
              <label className="font-bold">Proje İşbirliği:</label>
              <div
                dangerouslySetInnerHTML={{
                  __html: fixQuillLists(data.projeIsbirlik),
                }}
              />
            </div>
            <div className="field">
              <label className="font-bold">Proje Ekibi:</label>
              <div
                dangerouslySetInnerHTML={{
                  __html: fixQuillLists(data.projeEkip),
                }}
              />
            </div>
            <div className="field">
              <label className="font-bold">İnşaat Alanı:</label>
              <div>{data.insaatAlan}</div>
            </div>
          </div>
          <div className="field md:col-6">
            <div className="field">
              <label className="font-bold">Kapak Görsel:</label>
              <img
                src={data.kapakGorsel}
                alt="Kapak"
                className="w-10rem shadow-2 block cursor-pointer"
                onClick={() => handleImageClick(data.kapakGorsel)}
              />
            </div>
            <div className="field mt-3">
              <label className="font-bold">Yatay Görsel:</label>
              <img
                src={data.yatayGorsel}
                alt="Yatay"
                className="w-10rem shadow-2 block cursor-pointer"
                onClick={() => handleImageClick(data.yatayGorsel)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setPhotoZoomVisible(true);
  };

  const showDetailPopup = (rowData: Proje) => {
    setSelectedDetail({
      Başlık: rowData.baslik,
      Açıklama: rowData.aciklama,
      "Proje Yeri": rowData.projeYer,
      "Proje Yılı": new Date(rowData.projeYil).toLocaleDateString(),
      "Proje İşbirliği": rowData.projeIsbirlik,
      "Proje Ekibi": rowData.projeEkip,
      "İnşaat Alanı": rowData.insaatAlan,
    });
    setDetailPopupVisible(true);
  };

  const editorSettings = editorHeader();

  return (
    <div className="card p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4">
        <h2 className="text-xl">Proje Yönetimi</h2>
        <Button
          label="Yeni Proje Ekle"
          icon="pi pi-plus"
          onClick={() => showDialog()}
        />
      </div>

      <DataTable
        value={items}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="id"
        loading={loading}
        paginator
        rows={10}
        emptyMessage="Proje bulunamadı"
        rowsPerPageOptions={[5, 10, 25, 50]}
        sortField="projeYil"
        sortOrder={-1}
      >
        <Column expander style={{ width: "3rem" }} />
        <Column
          field="baslik"
          header="Başlık"
          sortable
          style={{ width: "20%" }}
        />
        <Column
          field="aciklama"
          header="Açıklama"
          sortable
          style={{ width: "30%" }}
          body={(rowData) => (
            <div
              className="cursor-pointer"
              style={{ whiteSpace: "pre-line" }}
              onClick={() => {
                showDetailPopup(rowData);
              }}
            >
              {rowData.aciklama.length > 100 ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fixQuillLists(rowData.aciklama.substring(0, 100)),
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fixQuillLists(rowData.aciklama),
                  }}
                />
              )}
              <div className="cursor-pointer" style={{ color: "#ed145b" }}>
                Detaya Git &gt;
              </div>
            </div>
          )}
        />
        <Column
          field="projeYil"
          header="Yıl"
          sortable
          style={{ width: "10%" }}
          body={(rowData) => new Date(rowData.projeYil).toLocaleDateString()}
        />
        <Column
          field="temaTipleriAdi"
          header="Tema"
          sortable
          style={{ width: "15%" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "15%" }} />
      </DataTable>

      {/* Ana Dialog */}
      <Dialog
        baseZIndex={100}
        visible={dialogVisible}
        header={editMode ? "Proje Düzenle" : "Yeni Proje Ekle"}
        modal
        className="p-fluid w-full md:w-6"
        onHide={hideDialog}
      >
        <div className="field mt-4">
          <label htmlFor="baslik">Başlık</label>
          <InputText
            id="baslik"
            value={formData.baslik}
            onChange={(e) =>
              setFormData({ ...formData, baslik: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="aciklama">Açıklama</label>
          <Editor
            id="aciklama"
            value={formData.aciklama}
            headerTemplate={editorSettings}
            onTextChange={(e) =>
              setFormData({ ...formData, aciklama: e.htmlValue || "" })
            }
            style={{ height: "200px" }}
          />
        </div>

        <div className="field">
          <label htmlFor="projeYer">Proje Yeri</label>
          <InputText
            id="projeYer"
            value={formData.projeYer}
            onChange={(e) =>
              setFormData({ ...formData, projeYer: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="projeYil">Proje Yılı</label>
          <InputText
            id="projeYil"
            type="date"
            value={formData.projeYil.split("T")[0]}
            onChange={(e) =>
              setFormData({
                ...formData,
                projeYil: new Date(e.target.value).toISOString(),
              })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="projeIsbirlik">Proje İşbirliği</label>
          <Editor
            id="projeIsbirlik"
            value={formData.projeIsbirlik}
            headerTemplate={editorSettings}
            onTextChange={(e) =>
              setFormData({ ...formData, projeIsbirlik: e.htmlValue || "" })
            }
            style={{ height: "200px" }}
          />
        </div>

        <div className="field">
          <label htmlFor="projeEkip">Proje Ekibi</label>
          <Editor
            id="projeEkip"
            value={formData.projeEkip}
            headerTemplate={editorSettings}
            onTextChange={(e) =>
              setFormData({ ...formData, projeEkip: e.htmlValue || "" })
            }
            style={{ height: "200px" }}
          />
        </div>

        <div className="field">
          <label htmlFor="insaatAlan">İnşaat Alanı</label>
          <InputText
            id="insaatAlan"
            value={formData.insaatAlan}
            onChange={(e) =>
              setFormData({ ...formData, insaatAlan: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="temaTipId">Tema</label>
          <Dropdown
            id="temaTipId"
            value={formData.temaTipId}
            options={temaList}
            onChange={(e) => setFormData({ ...formData, temaTipId: e.value })}
            optionLabel="baslik"
            optionValue="id"
            placeholder="Tema Seçiniz"
          />
        </div>

        <div className="field">
          <label htmlFor="kapakGorsel">Kapak Görsel</label>
          {editMode && formData.kapakGorsel && (
            <img
              src={formData.kapakGorsel}
              alt="Kapak Görsel"
              className="w-10rem mb-3 cursor-pointer"
              onClick={() => handleImageClick(formData.kapakGorsel)}
            />
          )}
          <FileUpload
            mode="basic"
            name="kapakGorsel"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel={editMode ? "Görseli Değiştir" : "Görsel Seç"}
            onSelect={(e) =>
              setFormData({
                ...formData,
                kapakFotoFile: e.files[0],
              })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="yatayGorsel">Yatay Görsel</label>
          {editMode && formData.yatayGorsel && (
            <img
              src={formData.yatayGorsel}
              alt="Yatay Görsel"
              className="w-10rem mb-3 cursor-pointer"
              onClick={() => handleImageClick(formData.yatayGorsel)}
            />
          )}
          <FileUpload
            mode="basic"
            name="yatayGorsel"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel={editMode ? "Görseli Değiştir" : "Görsel Seç"}
            onSelect={(e) =>
              setFormData({
                ...formData,
                yatayFotoFile: e.files[0],
              })
            }
          />
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label="İptal"
            icon="pi pi-times"
            outlined
            onClick={hideDialog}
            className="mr-2"
          />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSubmit} />
        </div>
      </Dialog>

      {/* Fotoğraf Yönetimi Dialog */}
      <Dialog
        baseZIndex={100}
        visible={fotoDialogVisible}
        header={`${selectedItem?.baslik} ait fotoğraflar`}
        modal
        className="p-fluid w-full md:w-8"
        onHide={() => {
          setFotoDialogVisible(false);
          setSelectedFotos([]);
          setSelectedItem(null);
        }}
      >
        <div className="mb-4">
          <FileUpload
            mode="basic"
            name="foto"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel="Fotoğraf Ekle"
            customUpload
            uploadHandler={async (event) => {
              await handleFotoUpload(event);
              // Dosyayı temizle
              if (event.files) {
                event.options.clear();
              }
            }}
          />
        </div>

        <div className="grid">
          {selectedFotos.map((foto) => (
            <div key={foto.id} className="field md:col-4 lg:col-3 p-2">
              <div className="relative">
                <img
                  src={foto.dosya}
                  alt=""
                  className="w-full shadow-2 border-round cursor-pointer bg-gray-50"
                  onClick={() => handleImageClick(foto.dosya)}
                />
                <Button
                  icon="pi pi-trash"
                  rounded
                  severity="danger"
                  className="absolute top-0 right-0 m-2"
                  onClick={() => handleFotoDelete(foto.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </Dialog>

      {photoZoomVisible && selectedImage && (
        <PhotoZoom
          src={selectedImage}
          onClose={() => {
            setPhotoZoomVisible(false);
            setSelectedImage(null);
          }}
        />
      )}

      <DetailPopup
        isOpen={detailPopupVisible}
        onClose={() => setDetailPopupVisible(false)}
        data={selectedDetail}
        header="Proje Detayı"
      />
    </div>
  );
};

export default ProjeYonetimi;
